import React, { useEffect, useState } from 'react';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { Check, X } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { featureFlagsReducer } from '../../store/reducers/featureFlags.reducer';
import { getIsUpdatingFeatureFlag } from '../../store/selectors/featureFlags.selector';
import { getAllUsers } from '../../store/selectors/user.selector';
import { greyP5 } from '../../theme.ts';
import { FeatureFlag, UpdateFeatureFlagRequest } from '../../types/featureFlags';
import { User } from '../../types/user';

const { updateFeatureFlag } = featureFlagsReducer.actions;

interface EditFeatureFlagModalProps {
  open: boolean;
  onClose: () => void;
  featureFlag: FeatureFlag;
}

export const EditFeatureFlagModal = ({ open, onClose, featureFlag }: EditFeatureFlagModalProps) => {
  const dispatch = useAppDispatch();
  const isUpdating = useAppSelector(getIsUpdatingFeatureFlag);
  const allUsers = useAppSelector(getAllUsers);

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    enabled: boolean;
    rolloutPercentage: number;
    selectedUsers: User[];
  }>({
    name: '',
    description: '',
    enabled: false,
    rolloutPercentage: 0,
    selectedUsers: [],
  });

  const [errors, setErrors] = useState<{
    name: string;
    description: string;
    rolloutPercentage: string;
  }>({
    name: '',
    description: '',
    rolloutPercentage: '',
  });

  // Initialize form data when modal opens or feature flag changes
  useEffect(() => {
    if (open && featureFlag) {
      const selectedUsers = featureFlag.targets
        .map((target) => allUsers.find((user) => user.id === target.userId))
        .filter((user) => user !== undefined) as User[];

      setFormData({
        name: featureFlag.name,
        description: featureFlag.description,
        enabled: featureFlag.enabled,
        rolloutPercentage: featureFlag.rolloutPercentage,
        selectedUsers,
      });

      // Clear errors when reopening
      setErrors({
        name: '',
        description: '',
        rolloutPercentage: '',
      });
    }
  }, [open, featureFlag, allUsers]);

  const validateForm = (): boolean => {
    const newErrors = {
      name: '',
      description: '',
      rolloutPercentage: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.rolloutPercentage < 0 || formData.rolloutPercentage > 100) {
      newErrors.rolloutPercentage = 'Rollout percentage must be between 0 and 100';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const updateRequest: UpdateFeatureFlagRequest = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      enabled: formData.enabled,
      rolloutPercentage: formData.rolloutPercentage,
      userTargets: formData.selectedUsers.map((user) => user.id),
    };

    dispatch(
      updateFeatureFlag({
        id: featureFlag.id,
        request: updateRequest,
      }),
    );
    onClose();
  };

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
  };

  // Handle Esc key to close the modal
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && !isUpdating) {
      event.stopPropagation();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
      onKeyDown={handleKeyDown}
    >
      <Box
        sx={{
          backgroundColor: greyP5,
          borderRadius: 2,
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white', lineHeight: 1.2 }}>
              Edit Feature Flag
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: 'monospace', display: 'block' }}
            >
              {featureFlag.key}
            </Typography>
          </Box>
          <button onClick={handleClose} className="text-gray-400 hover:text-white" disabled={isUpdating}>
            <X size={20} />
          </button>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
          }}
        >
          <TextField
            size="small"
            required
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
            disabled={isUpdating}
          />

          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            error={!!errors.description}
            helperText={errors.description}
            disabled={isUpdating}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  disabled={isUpdating}
                />
              }
              label="Enabled"
              sx={{ m: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Rollout: {formData.rolloutPercentage}%
              </Typography>
              <Slider
                size="small"
                value={formData.rolloutPercentage}
                onChange={(_, newValue) => setFormData({ ...formData, rolloutPercentage: newValue as number })}
                step={1}
                min={0}
                max={100}
                valueLabelDisplay="auto"
                disabled={isUpdating}
                sx={{ mt: 0.5 }}
              />
              {errors.rolloutPercentage && (
                <Typography color="error" variant="caption">
                  {errors.rolloutPercentage}
                </Typography>
              )}
            </Box>
          </Box>

          <Autocomplete
            size="small"
            multiple
            options={allUsers}
            getOptionLabel={(option) => `${option.fullName} (${option.email})`}
            value={formData.selectedUsers}
            onChange={(_, newValue) => setFormData({ ...formData, selectedUsers: newValue })}
            disabled={isUpdating}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Target Users" placeholder="Select users to target" />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  size="small"
                  label={`${option.fullName} (${option.email})`}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Button onClick={handleClose} disabled={isUpdating} startIcon={<X size={16} />} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={14} /> : <Check size={16} />}
            size="small"
            sx={{
              bgcolor: '#B3EC49',
              color: 'black',
              '&:hover': { bgcolor: 'rgba(179, 236, 73, 0.8)' },
              borderRadius: '20px',
              px: 3,
            }}
          >
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </Box>
      </Box>
    </div>
  );
};
